import {IncomingMessage} from 'http'
import {jwtDecode} from 'jwt-decode'
import {Session} from 'next-auth/core/types'

import {getRequestSession} from '@/utils/auth/session'

// global singleton graphQLClientSessionManager stores actual access tokens for accessing Hasura

const TOKEN_EXPIRING_SOON_SECONDS = 30

class GraphQLClientSessionManager {
  private storedSession: Session | null = null

  private isTokenExpiringSoon(
    token: string | undefined,
    bufferSeconds: number = TOKEN_EXPIRING_SOON_SECONDS,
  ): boolean {
    if (!token) {
      return true
    }
    const decoded = jwtDecode(token)
    if (!decoded?.exp) {
      return true // treat undecodable tokens as expired
    }

    // `Date.now()` rather than luxon, which was the shell's last reason to load a date library.
    const nowSeconds = Date.now() / 1000
    return decoded.exp - nowSeconds <= bufferSeconds
  }

  // in-flight refresh promise - prevents duplicate calls to getFreshSession
  private getFreshSessionPromise: Promise<Session | null> | undefined = undefined

  // invokes next-auth call to /api/session to retrieve fresh session
  private async doGetFreshSession(ctx?: {req: IncomingMessage}): Promise<Session | null> {
    return await getRequestSession(ctx)
  }

  // invokes next-auth call to /api/session to retrieve fresh session; re-entrance protected
  private async getFreshSession(ctx?: {req: IncomingMessage}): Promise<Session | null> {
    // Deduplicate concurrent getFreshTokens calls
    if (this.getFreshSessionPromise) {
      return this.getFreshSessionPromise
    }
    this.getFreshSessionPromise = this.doGetFreshSession(ctx).finally(() => {
      this.getFreshSessionPromise = undefined
    })
    return this.getFreshSessionPromise
  }

  private pickToken(useAdminToken?: boolean, session?: Session): string | undefined {
    const _session = session ?? this.storedSession
    return useAdminToken
      ? _session?.accessToken
      : _session?.accessTokenAdminUser || _session?.accessToken
  }

  public async getToken(ctx?: {req: IncomingMessage}, useAdminToken?: boolean) {
    // try to reuse the current stored token
    const token = this.pickToken(useAdminToken)
    if (!this.isTokenExpiringSoon(token)) {
      return token // if valid, use it
    }

    // tokens must be refreshed
    this.storedSession = await this.getFreshSession(ctx)

    return this.pickToken(useAdminToken)
  }
}

export const graphQLClientSessionManager = new GraphQLClientSessionManager()
